/**
 * BottomTabBar — the native replacement for Sidebar.jsx's mobile overlay.
 *
 * A sidebar that slides in over the content works on the web (it's what
 * every visitor gets under 768px today), but inside the app it reads as a
 * website that got squeezed onto a phone rather than a native screen. Three
 * destinations, picked from what Sidebar.jsx already navigates to:
 *
 *   Ask JAI       → /chat, the front door of the app
 *   Your Leagues  → NativeLeaguesScreen.jsx, the full list of connected
 *                   leagues (mirrors Sidebar.jsx's "My Leagues" on web) plus
 *                   Add League — always the same screen, not a shortcut that
 *                   behaves differently depending on how many are connected.
 *   Settings      → the same SettingsModal AccountMenu opens, rendered in
 *                   place rather than through the account-menu popup
 *                   (there's no sidebar for that popup to float above of on
 *                   native).
 *
 * Gated on IS_NATIVE_APP only for tonight — see AppShell.jsx. Widening this
 * to the mobile *web* viewport too is a good idea but a visible change to
 * the live product during draft week, so that's left as a flag for Jasper
 * to flip when he's ready (see the ALSO_SHOW_ON_MOBILE_WEB constant below).
 */

import { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { JAI_COLORS, JAI_FONTS } from './JaiBrand'
import SettingsModal from './SettingsModal'
import NativeLeaguesScreen from './NativeLeaguesScreen'

// Flip this to `true` (and drop the IS_NATIVE_APP-only gate in AppShell.jsx)
// to also replace the mobile-web sidebar overlay with this tab bar. Left off
// tonight — see engineering/ios/IOS_OVERNIGHT_RUN.md Phase 2 Unsure list.
export const ALSO_SHOW_ON_MOBILE_WEB = false

const ICON = 22

const icons = {
  chat: (
    <svg width={ICON} height={ICON} viewBox="0 0 16 16" fill="none">
      <path d="M2 2h12a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H5l-3 2V3a1 1 0 0 1 1-1Z"
        stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
    </svg>
  ),
  // Trophy, not a sport ball — this tab covers every league regardless of
  // sport, so a basketball (the old icon) read as "this is the NBA tab".
  league: (
    <svg width={ICON} height={ICON} viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M8 12.2v1.6M6.2 13.8h3.6" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
      <path d="M5 2.5h6v3.3a3 3 0 0 1-6 0V2.5Z" stroke="currentColor" strokeWidth="1.35" strokeLinejoin="round" />
      <path d="M5 3.6H3.2v1.6a1.9 1.9 0 0 0 1.4 1.8M11 3.6h1.8v1.6a1.9 1.9 0 0 1-1.4 1.8"
        stroke="currentColor" strokeWidth="1.15" strokeLinecap="round" />
    </svg>
  ),
  settings: (
    <svg width={ICON} height={ICON} viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.7" />
      <path d="M12 2v3M12 19v3M4.2 4.2l2.1 2.1M17.7 17.7l2.1 2.1M2 12h3M19 12h3M4.2 19.8l2.1-2.1M17.7 6.3l2.1-2.1"
        stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  ),
}

function TabButton({ icon, label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 3,
        // 44pt minimum tap target (HIG) — the button fills its column and
        // the bar itself is tall enough that this height clears 44pt even
        // before safe-area padding is added.
        minHeight: 48,
        padding: '6px 4px',
        background: 'transparent',
        border: 'none',
        color: active ? JAI_COLORS.blueBright : 'rgba(246,248,251,0.62)',
        cursor: 'pointer',
      }}
    >
      {icon}
      <span style={{ fontFamily: JAI_FONTS.tight, fontSize: 11, fontWeight: 650 }}>{label}</span>
    </button>
  )
}

export default function BottomTabBar() {
  const location = useLocation()
  const navigate = useNavigate()
  const [showSettings, setShowSettings] = useState(false)
  // Always opens the full "Your Leagues" list now — requested directly, so
  // the tab shows the same thing every time rather than jumping straight to
  // a league when exactly one is connected and only opening Add League when
  // none are (the old shortcut behaviour, which read as inconsistent).
  const [showLeagues, setShowLeagues] = useState(false)

  return (
    <>
      <nav
        role="navigation"
        aria-label="Main"
        style={{
          position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 50,
          display: 'flex',
          background: JAI_COLORS.navyDeep,
          borderTop: '1px solid rgba(255,255,255,0.08)',
          boxShadow: '0 -4px 24px rgba(0,0,0,0.35)',
          // Clears the home indicator; the row's own 48pt min-height still
          // sits above that padding, so it never shrinks below 44pt.
          paddingBottom: 'var(--safe-bottom)',
          paddingLeft: 'var(--safe-left)',
          paddingRight: 'var(--safe-right)',
        }}
      >
        <TabButton
          icon={icons.chat}
          label="Ask JAI"
          active={location.pathname === '/chat' && !showLeagues && !showSettings}
          onClick={() => { setShowLeagues(false); setShowSettings(false); navigate('/chat') }}
        />
        <TabButton
          icon={icons.league}
          label="Your Leagues"
          active={showLeagues}
          onClick={() => { setShowSettings(false); setShowLeagues(true); window.dispatchEvent(new Event('jai:native-tab-left-chat')) }}
        />
        <TabButton
          icon={icons.settings}
          label="Settings"
          active={showSettings}
          onClick={() => { setShowLeagues(false); setShowSettings(true); window.dispatchEvent(new Event('jai:native-tab-left-chat')) }}
        />
      </nav>

      {showLeagues && <NativeLeaguesScreen onClose={() => setShowLeagues(false)} />}

      {showSettings && <SettingsModal onClose={() => setShowSettings(false)} />}
    </>
  )
}
